// @flow

import React, { Component } from 'react';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import * as Actions from 'main-app/store/Actions';
import CreateStripeCheckoutSessionMutation from 'main-app/mutations/CreateStripeCheckoutSession';
import EmailStripePaymentLinkMutation from 'main-app/mutations/EmailStripePaymentLink';
import Button from 'shared/components/common/Button';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'shared/components/modal';

const UrlBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
`;

const UrlText = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 13px;
  color: #333;
  background: #fafafa;
  cursor: text;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: #555;
  margin: 0 0 8px;
`;

const ErrorText = styled.p`
  font-size: 14px;
  color: #d9534f;
  margin: 0;
`;

const EmailSection = styled.div`
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
`;

const EmailLabel = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px;
`;

const EmailRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EmailInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 13px;
  color: #333;
  background: #fff;
  &:focus {
    outline: none;
    border-color: #40a9ff;
  }
`;

type Props = {
  onClose: () => void,
  invoiceId: string,
  customerEmail: ?string,
};

type State = {
  loading: boolean,
  sessionUrl: ?string,
  error: ?string,
  emailRecipient: string,
  emailSending: boolean,
  emailError: ?string,
};

class StripePaymentLinkModal extends Component<Props, State> {
  state = {
    loading: true,
    sessionUrl: null,
    error: null,
    emailRecipient: this.props.customerEmail || '',
    emailSending: false,
    emailError: null,
  };

  componentDidMount() {
    this.generateLink();
  }

  generateLink = () => {
    const { invoiceId } = this.props;
    this.setState({ loading: true, error: null, sessionUrl: null });

    CreateStripeCheckoutSessionMutation.commit({
      variables: { input: { invoiceId } },
    })
      .then((response: Object) => {
        const url =
          response &&
          response.createStripeCheckoutSession &&
          response.createStripeCheckoutSession.sessionUrl;
        this.setState({ loading: false, sessionUrl: url || null });
      })
      .catch((err: Object) => {
        const msg =
          (err && err.message) || i18n.t('Failed to generate payment link.');
        this.setState({ loading: false, error: msg });
      });
  };

  handleCopy = () => {
    const { sessionUrl } = this.state;
    if (!sessionUrl) return;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(sessionUrl).then(() => {
        Actions.alertNotification(i18n.t('Payment link copied to clipboard.'), 'success');
      });
    } else {
      // Fallback for older browsers
      const el = document.getElementById('stripe-payment-url-input');
      if (el) {
        el.select();
        document.execCommand('copy');
        Actions.alertNotification(i18n.t('Payment link copied to clipboard.'), 'success');
      }
    }
  };

  handleOpen = () => {
    const { sessionUrl } = this.state;
    if (sessionUrl) {
      window.open(sessionUrl, '_blank', 'noopener,noreferrer');
    }
  };

  handleEmailRecipientChange = (e: Object) => {
    this.setState({ emailRecipient: e.target.value, emailError: null });
  };

  handleSendEmail = () => {
    const { invoiceId } = this.props;
    const { emailRecipient } = this.state;

    if (!emailRecipient || !emailRecipient.trim()) {
      this.setState({ emailError: i18n.t('Please enter a recipient email address.') });
      return;
    }

    this.setState({ emailSending: true, emailError: null });

    EmailStripePaymentLinkMutation.commit({
      variables: {
        input: {
          invoiceId,
          customerEmail: emailRecipient.trim(),
        },
      },
    })
      .then(() => {
        this.setState({ emailSending: false });
        Actions.alertNotification(
          i18n.t('Payment link emailed successfully.'),
          'success',
        );
      })
      .catch((err: Object) => {
        const msg =
          (err && err.message) || i18n.t('Failed to send email. Please try again.');
        this.setState({ emailSending: false, emailError: msg });
      });
  };

  render() {
    const { onClose } = this.props;
    const {
      loading,
      sessionUrl,
      error,
      emailRecipient,
      emailSending,
      emailError,
    } = this.state;

    return (
      <Modal isOpen onClose={onClose}>
        <ModalHeader header={i18n.t('Send Payment Link')} onClose={onClose} />
        <ModalBody>
          {loading && (
            <InfoText>{i18n.t('Generating secure payment link…')}</InfoText>
          )}
          {error && (
            <ErrorText>{error}</ErrorText>
          )}
          {sessionUrl && (
            <>
              <InfoText>
                {i18n.t('Share this link with the customer. They will be taken to a secure Stripe checkout page to pay by credit card.')}
              </InfoText>
              <UrlBox>
                <UrlText
                  id="stripe-payment-url-input"
                  readOnly
                  value={sessionUrl}
                  onClick={e => e.target.select()}
                />
              </UrlBox>
              <EmailSection>
                <EmailLabel>{i18n.t('Email this link to customer')}</EmailLabel>
                <EmailRow>
                  <EmailInput
                    type="email"
                    placeholder={i18n.t('customer@example.com')}
                    value={emailRecipient}
                    onChange={this.handleEmailRecipientChange}
                    disabled={emailSending}
                  />
                  <Button
                    theme="blue"
                    size="small"
                    onClick={this.handleSendEmail}
                    disabled={emailSending}
                  >
                    {emailSending ? i18n.t('Sending…') : i18n.t('Send Email')}
                  </Button>
                </EmailRow>
                {emailError && (
                  <ErrorText style={{ marginTop: 6 }}>{emailError}</ErrorText>
                )}
              </EmailSection>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {error && (
            <Button
              theme="blue"
              size="small"
              onClick={this.generateLink}
            >
              {i18n.t('Retry')}
            </Button>
          )}
          {sessionUrl && (
            <>
              <Button
                theme="grey"
                size="small"
                onClick={this.handleCopy}
              >
                {i18n.t('Copy Link')}
              </Button>
              <Button
                theme="blue"
                size="small"
                onClick={this.handleOpen}
              >
                {i18n.t('Open Link')}
              </Button>
            </>
          )}
          <Button theme="grey" size="small" onClick={onClose}>
            {i18n.t('Close')}
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}

export default StripePaymentLinkModal;
